import React from "react";

/**
 * 判斷是否為「chunk 載入失敗」錯誤(webpack 動態 import 抓不到 chunk)。
 * 兼容:err.name === "ChunkLoadError"(webpack 標準),或訊息符合 "Loading chunk X failed"。
 * @param {unknown} err
 * @returns {boolean}
 */
export function isChunkLoadError(err) {
  if (!err) return false;
  if (err.name === "ChunkLoadError") return true;
  return /Loading chunk [\d\w-]+ failed/i.test(err.message || "");
}

/**
 * React.lazy 的強化版:處理「chunk 載入失敗」。
 *
 * 情境:路由 code-split 後,server 重編譯(開發期)或正式環境重新部署時,
 * chunk 的內容雜湊會變;開著舊分頁的使用者一觸發 lazy import,會去抓已不存在的
 * 舊 chunk → ChunkLoadError → 整頁掛掉。
 *
 * 策略:第一次遇到 chunk 錯誤 → 自動整頁重載一次(抓到新的 manifest 即恢復);
 * 用 sessionStorage 記錄「已重載過」避免無限重載——若重載後仍失敗(例如 server
 * 真的掛了),則把錯誤往上拋,交給 AppErrorBoundary 顯示友善 fallback。
 *
 * @param {() => Promise<{ default: React.ComponentType }>} importer 動態 import 函式
 * @returns {React.LazyExoticComponent<React.ComponentType>}
 */
export function lazyWithRetry(importer) {
  const RELOAD_KEY = "dck:chunk-reload-attempted";

  return React.lazy(async () => {
    try {
      const mod = await importer();
      // 成功載入 → 清掉重載旗標,下次錯誤才會再給一次重載機會
      window.sessionStorage.removeItem(RELOAD_KEY);
      return mod;
    } catch (err) {
      const alreadyReloaded =
        window.sessionStorage.getItem(RELOAD_KEY) === "1";

      if (isChunkLoadError(err) && !alreadyReloaded) {
        window.sessionStorage.setItem(RELOAD_KEY, "1");
        window.location.reload();
        // 回傳永不 resolve 的 promise:重載前不要 render 任何東西
        return new Promise(() => {});
      }

      // 非 chunk 錯誤,或重載後仍失敗 → 交給 ErrorBoundary
      throw err;
    }
  });
}
